import axios from 'axios';
import { detectEnv } from './diagnostics';

// Patient PRE-join funnel. Diagnostics (diagnostics.js) only starts once the
// patient created the peerConnection — before that we're blind. This logger
// records where the patient GIVES UP before reaching WebRTC: media denied,
// granted but didn't join, or joined but the room/offer never appeared (doctor
// absent). Writes to RTDB (via the recordTelemedPatientFunnel cloud function)
// and only persists drop-offs — whoever reaches WebRTC is covered by diagnostics.

const ROOT_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_ROOT_URL;
const MAX_STEPS = 20;

let funnel = null;

function addStep(step, extra) {
  if (!funnel) return;
  funnel.lastStep = step;
  funnel.steps.push({ step, t: Date.now(), ...(extra || {}) });
  if (funnel.steps.length > MAX_STEPS) funnel.steps.shift();
}

export function startPatientFunnel({ roomId, mainUser } = {}) {
  funnel = {
    roomId: roomId || null,
    mainUser: mainUser || null,
    env: detectEnv(),
    steps: [],
    lastStep: null,
    mediaGranted: null,
    mediaErrorName: null,
    reachedWebrtc: false,
    createdAt: new Date().toISOString(),
    endedAt: null,
  };
  addStep('media_requested');
}

export function markFunnelMedia(granted, errorName) {
  if (!funnel) return;
  funnel.mediaGranted = !!granted;
  funnel.mediaErrorName = errorName || null;
  addStep(granted ? 'media_granted' : 'media_denied', errorName ? { errorName } : undefined);
}

export function markFunnelEntered() {
  addStep('entered');
}

export function markFunnelReachedWebrtc() {
  if (!funnel) return;
  funnel.reachedWebrtc = true;
  addStep('reached_webrtc');
}

function recordPatientFunnelRequest(payload) {
  return axios.post(`${ROOT_URL}/recordTelemedPatientFunnel`, payload);
}

// Flushes the funnel. Only persists if the patient did NOT reach WebRTC
// (drop-off); if they did, diagnostics covers it and we discard. lastStep says
// where they stopped: media_denied (permission), media_granted (didn't click
// Acessar), entered (joined but the room/offer never appeared = doctor absent).
export async function flushPatientFunnel(reason) {
  if (!funnel) return;
  if (funnel.reachedWebrtc) {
    funnel = null;
    return;
  }
  try {
    funnel.reason = reason || null;
    funnel.endedAt = new Date().toISOString();
    if (funnel.mainUser && funnel.roomId) {
      await recordPatientFunnelRequest({
        mainUser: funnel.mainUser,
        roomId: funnel.roomId,
        funnel: JSON.parse(JSON.stringify(funnel)),
      });
    }
  } catch (error) {
    console.warn('flushPatientFunnel failed', error);
  } finally {
    funnel = null;
  }
}

// Emergency flush on unload (pagehide): like flushPatientFunnel but via keepalive
// — axios is killed on tab close, which is exactly when the drop-off happens.
// Keeps the drop-off-only gate. Idempotent (backend .set()). Does NOT clear the
// funnel: bfcache may bring the page back.
export function flushPatientFunnelBeacon(reason) {
  if (!funnel || !ROOT_URL) return;
  if (funnel.reachedWebrtc) return;
  try {
    funnel.reason = reason || null;
    funnel.endedAt = new Date().toISOString();
    if (funnel.mainUser && funnel.roomId) {
      const payload = {
        mainUser: funnel.mainUser,
        roomId: funnel.roomId,
        funnel: JSON.parse(JSON.stringify(funnel)),
      };
      fetch(`${ROOT_URL}/recordTelemedPatientFunnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (error) {
    console.warn('flushPatientFunnelBeacon failed', error);
  }
}
